'use client';
/**
 * SharedAppShell — единый дизайн-компонент для всех TeamBuilder фронтов.
 *
 * Clinical Modern: тёмный sidebar (#0b1120), indigo-акценты, тонкие line-иконки,
 * мини-профиль внизу. Каждый app передаёт свою конфигурацию навигации.
 */
import { ReactNode, useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

// ── SVG icon set (strokeWidth 1.8, round caps) ───────────────
const Icons: Record<string, ReactNode> = {
  grid: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></svg>,
  building: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16M15 21V11a2 2 0 012-2h2a2 2 0 012 2v10"/><path d="M9 7h2M9 11h2M9 15h2"/></svg>,
  users: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5"/><path d="M3 21v-1a6 6 0 0112 0v1"/><circle cx="17" cy="8" r="3"/><path d="M15.5 13.5a6 6 0 015.5 5.5v1"/></svg>,
  chart: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 14l3-4 3 3 4-6"/></svg>,
  target: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/></svg>,
  briefcase: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M3 12h18"/></svg>,
  puzzle: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3h4a1 1 0 011 1v3a2 2 0 104 0V4a1 1 0 011 1v3a2 2 0 002 2 2 2 0 002-2h-3a1 1 0 01-1-1V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3a2 2 0 11-4 0V4a1 1 0 00-1-1z"/></svg>,
  gear: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 01-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 01-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 012.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 012.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z"/></svg>,
  book: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>,
  tag: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><circle cx="7" cy="7" r="1"/></svg>,
  shield: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  scale: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M5 7l-3 6h6zM19 7l-3 6h6zM5 7h14"/></svg>,
  log: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h5"/></svg>,
  plug: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2v6M15 2v6M5 8h14v3a7 7 0 01-14 0V8zM12 15v7"/></svg>,
  server: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="8" rx="2"/><rect x="2" y="13" width="20" height="8" rx="2"/><path d="M6 7h.01M6 17h.01"/></svg>,
  dna: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 3c0 5 16 13 16 18M20 3c0 5-16 13-16 18M7 5h10M7 19h10M9 9h6M9 15h6"/></svg>,
  rocket: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09zM12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2zM9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>,
  brain: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0112 4.5v15a2.5 2.5 0 01-4.96.44 2.5 2.5 0 01-2.96-3.08 3 3 0 01-.34-5.58 2.5 2.5 0 011.32-4.24 2.5 2.5 0 014.44-.83zM9.5 2A2.5 2.5 0 0112 4.5v15a2.5 2.5 0 004.96.44 2.5 2.5 0 002.96-3.08 3 3 0 00.34-5.58 2.5 2.5 0 00-1.32-4.24 2.5 2.5 0 00-4.44-.83A2.5 2.5 0 0014.5 2" /></svg>,
  alert: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  check: <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
};

export function getIcon(name: string): ReactNode {
  return Icons[name] ?? Icons.grid;
}

// ── Types ─────────────────────────────────────────────────────
export type NavLeaf = { href: string; label: string; match?: string; icon?: string; badge?: number };
export type NavGroup = { label: string; icon?: string; children: NavLeaf[] };
export type NavItem = NavGroup;

export type ShellConfig = {
  /** Brand label shown at top of sidebar (e.g. "B2B", "ЛК", "Admin") */
  brandLabel?: string;
  /** Navigation items */
  nav: NavItem[];
  /** User info for footer */
  userEmail?: string;
  /** User display name for footer (optional, falls back to email) */
  userName?: string;
  /** Logout handler */
  onLogout?: () => void;
  /** Extra header content (e.g. company switcher) */
  headerExtra?: ReactNode;
  /** Login route to skip shell on */
  loginRoute?: string;
};

// ── Helpers ───────────────────────────────────────────────────
function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function resolveHref(href: string, companyId?: string | null): string {
  if (!href.includes('{id}')) return href;
  if (companyId) return href.replace('{id}', companyId);
  return '/';
}

function getInitial(email?: string): string {
  if (!email) return '?';
  return email[0].toUpperCase();
}

// ── Component ─────────────────────────────────────────────────
export function SharedAppShell({
  config,
  children,
}: {
  config: ShellConfig;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const loginRoute = config.loginRoute ?? '/login';
  const isLogin = pathname === loginRoute;
  useEffect(() => { setOpen(false); }, [pathname]);
  if (isLogin) return <>{children}</>;

  // Extract company ID from URL for {id} resolution
  const m = pathname.match(/^\/companies\/([^/]+)/);
  const companyId = m ? m[1] : null;

  const childActive = (c: NavLeaf, resolvedHref: string) => {
    const re = new RegExp(c.match ?? '^' + escapeRe(resolvedHref) + '$');
    return re.test(pathname);
  };

  const displayName = config.userName || config.userEmail?.split('@')[0] || 'Профиль';

  const SidebarContent = ({ onNav }: { onNav?: () => void }) => (
    <>
      {/* Brand */}
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-white/[0.06] shrink-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-lg shadow-indigo-500/20">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="8" r="3.5"/><path d="M3 21v-1a6 6 0 0112 0v1"/><circle cx="17" cy="8" r="3"/><path d="M15.5 13.5a6 6 0 015.5 5.5v1"/>
          </svg>
        </div>
        <div className="leading-tight">
          <div className="text-[13px] font-semibold text-white">TeamBuilder</div>
          <div className="text-[10px] text-white/30">{config.brandLabel ?? 'Кабинет'}</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {config.nav.map((g, gi) => (
          <div key={g.label}>
            {gi > 0 && <div className="pt-4 pb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-white/20">{g.label}</div>}
            {g.children.map((c) => {
              const resolved = resolveHref(c.href, companyId);
              const active = childActive(c, resolved);
              return (
                <Link key={c.href + c.label} href={resolved} onClick={onNav}
                  className={`group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-all ${
                    active
                      ? 'bg-indigo-500/[0.12] font-medium text-white'
                      : 'text-white/45 hover:text-white/90 hover:bg-white/[0.04]'
                  }`}>
                  {active && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-indigo-400" />
                  )}
                  {c.icon && (
                    <span className={active ? 'text-indigo-300' : 'opacity-80'}>{getIcon(c.icon)}</span>
                  )}
                  <span>{c.label}</span>
                  {c.badge != null && (
                    <span className="ml-auto rounded-full bg-white/[0.08] px-1.5 py-0.5 text-[10px] font-medium text-white/40">{c.badge}</span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User card */}
      {config.userEmail != null && (
        <div className="border-t border-white/[0.06] p-3 shrink-0">
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-white/[0.04] transition-colors">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-xs font-semibold text-white shrink-0">{getInitial(config.userEmail)}</div>
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-[12px] font-medium text-white/90 truncate">{displayName}</div>
              <div className="text-[10px] text-white/30 truncate">{config.userEmail}</div>
            </div>
            {config.onLogout && (
              <button onClick={config.onLogout} title="Выйти"
                className="w-7 h-7 flex items-center justify-center rounded-md text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors shrink-0">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#f8fafc]">
      {/* Desktop sidebar */}
      <aside className="fixed bottom-0 left-0 top-0 z-30 hidden w-[260px] flex-col bg-[#0b1120] border-r border-white/[0.06] md:flex">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-[260px] flex-col bg-[#0b1120]">
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/[0.06]">
              <span className="text-[13px] font-semibold text-white">TeamBuilder</span>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white/80">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="flex-1 flex flex-col">
              <SidebarContent onNav={() => setOpen(false)} />
            </div>
          </aside>
        </div>
      )}

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col md:ml-[260px]">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/80 backdrop-blur px-4">
          <button onClick={() => setOpen(true)} className="text-slate-600 md:hidden">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          </button>
          {config.headerExtra}
          <div className="flex-1" />
        </header>
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
}
